import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { get } from '../api/http';
import { CHECKOUT_SESSION_STATUS_URL } from '../constants/apiUrls';
import './PaymentReturn.css';

interface SessionStatus {
  status: 'complete' | 'processing' | 'expired' | 'open';
  payment_status: string;
  event_title?: string;
  tickets?: Array<{
    ticket_type: string;
    quantity: number;
  }>;
  total_amount?: number;
  message?: string;
}

const PaymentReturn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<SessionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkSessionStatus = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await get<SessionStatus>(CHECKOUT_SESSION_STATUS_URL(sessionId));
        setStatus(response.data);

        // Clear local storage if payment is successful/complete
        if (response.data.status === 'complete') {
          localStorage.removeItem('checkoutSessionId');
          localStorage.removeItem('checkoutSessionExpiry');
          
          // Optionally clear email if you want to force re-entry, 
          // but typically we keep it for convenience. 
          // The user asked to "clear session", which strictly implies the checkout session data.
        }

      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to verify payment status');
      } finally {
        setLoading(false);
      }
    };

    checkSessionStatus();
  }, [sessionId]);

  // Reload the page once after a successful payment so the app picks up
  // fresh state (e.g. updated ticket availability) once the user has seen
  // the confirmation. Guarded by sessionStorage so this only fires once
  // per session_id and doesn't loop after the reload re-runs this effect.
  useEffect(() => {
    if (status?.status !== 'complete' || !sessionId) return;

    const reloadKey = `payment_return_reloaded_${sessionId}`;
    if (sessionStorage.getItem(reloadKey)) return;

    const timer = setTimeout(() => {
      sessionStorage.setItem(reloadKey, 'true');
      window.location.reload();
    }, 3000);

    return () => clearTimeout(timer);
  }, [status, sessionId]);

  if (loading) {
    return (
      <div className="payment-return-page">
        <div className="payment-status-card">
          <div className="spinner-large"></div>
          <h2>Verifying your payment...</h2>
          <p>Please wait while we confirm your booking</p>
        </div>
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="payment-return-page">
        <div className="payment-status-card error">
          <i className="fas fa-exclamation-circle"></i>
          <h2>Payment Verification Failed</h2>
          <p>{error || 'Unable to verify your payment status'}</p>
          <button onClick={() => navigate('/events')} className="return-btn">
            Return to Events
          </button>
        </div>
      </div>
    );
  }

  if (status.status === 'complete') {
    return (
      <div className="payment-return-page">
        <div className="payment-status-card success">
          <div className="success-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <h2>Payment Successful!</h2>
          <p className="success-message">
            Your tickets have been confirmed. Check your email for confirmation details.
          </p>
          
          {status.event_title && (
            <div className="booking-details">
              <h3>{status.event_title}</h3>
              {status.tickets && (
                <div className="ticket-list">
                  {status.tickets.map((ticket, index) => (
                    <div key={index} className="ticket-item">
                      <span>{ticket.ticket_type}</span>
                      <span>x{ticket.quantity}</span>
                    </div>
                  ))}
                </div>
              )}
              {status.total_amount && (
                <div className="total-paid">
                  <span>Total Paid:</span>
                  <span className="amount">${(status.total_amount / 100).toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          <button onClick={() => navigate('/events')} className="return-btn primary">
            Browse More Events
          </button>
        </div>
      </div>
    );
  }

  if (status.status === 'processing') {
    return (
      <div className="payment-return-page">
        <div className="payment-status-card processing">
          <div className="spinner-large"></div>
          <h2>Payment Processing</h2>
          <p>Your payment is being processed. You will receive a confirmation email shortly.</p>
          <button onClick={() => navigate('/events')} className="return-btn">
            Return to Events
          </button>
        </div>
      </div>
    );
  }

  if (status.status === 'expired') {
    return (
      <div className="payment-return-page">
        <div className="payment-status-card error">
          <i className="fas fa-clock"></i>
          <h2>Session Expired</h2>
          <p>Your checkout session has expired. Please try booking again.</p>
          <button onClick={() => navigate('/events')} className="return-btn">
            Return to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-return-page">
      <div className="payment-status-card">
        <i className="fas fa-info-circle"></i>
        <h2>Payment Status Unknown</h2>
        <p>{status.message || 'Unable to determine payment status'}</p>
        <button onClick={() => navigate('/events')} className="return-btn">
          Return to Events
        </button>
      </div>
    </div>
  );
};

export default PaymentReturn;
